import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
  ArrowLeft, Calendar, User, Clock, MapPin, Tag, Share2, BookOpen,
  ArrowRight, Newspaper, AlertCircle,
} from "lucide-react"
import { SiteShell } from "../../components/site/SiteShell"
import { Card, CardContent } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"
import { formatDate, formatDateTime } from "../../lib/constants"
import { api } from "../../lib/api"
import { toast } from "sonner"

interface Article {
  id: string
  title: string
  slug: string
  excerpt: string
  content: string
  category: string
  image_url?: string
  author?: string
  status: string
  published_at: number
  event_date?: number
  event_location?: string
}

export default function NewsArticle() {
  const { slug } = useParams<{ slug: string }>()
  const [article, setArticle] = useState<Article | null>(null)
  const [recent, setRecent] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) return
    setLoading(true)
    setError(null)
    api.get<{ article: Article; recent: any[] }>(`/api/public/news/${slug}`)
      .then(res => {
        setArticle(res.article)
        setRecent(res.recent || [])
      })
      .catch(err => {
        setError(err?.message || "Article not found")
      })
      .finally(() => setLoading(false))
  }, [slug])

  function share() {
    if (navigator.share) {
      navigator.share({ title: article?.title, url: window.location.href }).catch(() => {})
    } else {
      navigator.clipboard.writeText(window.location.href)
      toast.success("Link copied to clipboard")
    }
  }

  if (loading) {
    return (
      <SiteShell>
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-3xl mx-auto animate-pulse space-y-4">
            <div className="h-6 bg-ink-200 dark:bg-ink-800 rounded w-1/3" />
            <div className="h-12 bg-ink-200 dark:bg-ink-800 rounded w-3/4" />
            <div className="aspect-[16/9] bg-ink-200 dark:bg-ink-800 rounded-xl" />
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-4 bg-ink-200 dark:bg-ink-800 rounded" />)}
            </div>
          </div>
        </div>
      </SiteShell>
    )
  }

  if (error || !article) {
    return (
      <SiteShell>
        <div className="container mx-auto px-4 py-20">
          <Card className="border-0 shadow-md max-w-2xl mx-auto text-center">
            <CardContent className="py-16">
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 grid place-items-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="font-display font-bold text-2xl text-ink-900 dark:text-ink-100 mb-2">Article Not Found</h1>
              <p className="text-sm text-ink-500 dark:text-ink-400 mb-6">
                {error || "The article you are looking for does not exist or has been removed."}
              </p>
              <Link to="/news">
                <Button className="bg-brand-800 dark:bg-brand-700">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to News
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </SiteShell>
    )
  }

  const isEvent = article.category === "EVENT"

  return (
    <SiteShell>
      {/* Hero */}
      <section className="bg-brand-900 dark:bg-brand-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-30" />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-gold-500/10 rounded-full blur-3xl" />
        <div className="container mx-auto px-4 py-16 relative">
          <Link to="/news" className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-gold-300 mb-4">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to News & Events
          </Link>
          <div className="flex items-center gap-2 mb-4">
            <Badge variant={isEvent ? "warning" : "success"}>{article.category}</Badge>
            <span className="text-xs text-white/70 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {formatDate(article.published_at)}
            </span>
            {article.author && (
              <span className="text-xs text-white/70 flex items-center gap-1">
                <User className="w-3 h-3" /> {article.author}
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-bold leading-tight max-w-4xl mb-4">{article.title}</h1>
          <p className="text-base md:text-lg text-white/80 max-w-3xl leading-relaxed">{article.excerpt}</p>
        </div>
      </section>

      {/* Event banner */}
      {isEvent && (article.event_date || article.event_location) && (
        <section className="bg-gold-50 dark:bg-gold-900/20 border-y border-gold-200 dark:border-gold-800">
          <div className="container mx-auto px-4 py-5">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-5 h-5 text-gold-700 dark:text-gold-400" />
                <div>
                  <div className="text-xs text-ink-500 dark:text-ink-400 uppercase tracking-wider">Event Date</div>
                  <div className="font-semibold text-ink-900 dark:text-ink-100">
                    {article.event_date ? formatDateTime(article.event_date) : "To be announced"}
                  </div>
                </div>
              </div>
              {article.event_location && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-5 h-5 text-gold-700 dark:text-gold-400" />
                  <div>
                    <div className="text-xs text-ink-500 dark:text-ink-400 uppercase tracking-wider">Venue</div>
                    <div className="font-semibold text-ink-900 dark:text-ink-100">{article.event_location}</div>
                  </div>
                </div>
              )}
              <div className="ml-auto">
                <Button size="sm" onClick={share} variant="outline">
                  <Share2 className="w-4 h-4 mr-1.5" /> Share
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Body + sidebar */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-10">
            <article className="lg:col-span-2">
              {article.image_url && (
                <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-lg mb-8">
                  <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" />
                </div>
              )}

              <div className="prose prose-ink max-w-none">
                {article.content?.split("\n").map((para, i) => (
                  para.trim() ? <p key={i} className="text-ink-700 dark:text-ink-300 leading-relaxed mb-4 text-base">{para}</p> : null
                ))}
              </div>

              <div className="mt-10 pt-6 border-t border-ink-200 dark:border-ink-800 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-ink-400" />
                  <Badge variant="outline">{article.category}</Badge>
                </div>
                <Button onClick={share} variant="outline" size="sm">
                  <Share2 className="w-4 h-4 mr-1.5" /> Share Article
                </Button>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="space-y-6">
              <Card className="border-0 shadow-md">
                <CardContent className="pt-6">
                  <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-brand-700 dark:text-brand-400" />
                    Recent Articles
                  </h3>
                  {recent.length === 0 ? (
                    <p className="text-sm text-ink-500 dark:text-ink-400">No other articles available.</p>
                  ) : (
                    <div className="space-y-4">
                      {recent.map(r => (
                        <Link key={r.id} to={`/news/${r.slug}`} className="block group">
                          <div className="flex gap-3">
                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-ink-100 dark:bg-ink-800 shrink-0">
                              {r.image_url ? (
                                <img src={r.image_url} alt={r.title} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full grid place-items-center bg-brand-50 dark:bg-brand-900/30">
                                  <Newspaper className="w-5 h-5 text-brand-400" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 mb-1">
                                <Badge variant={r.category === "EVENT" ? "warning" : "success"} className="text-[10px]">{r.category}</Badge>
                                <span className="text-[10px] text-ink-400">{formatDate(r.published_at)}</span>
                              </div>
                              <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-100 line-clamp-2 group-hover:text-brand-700 dark:group-hover:text-brand-400 transition-colors">
                                {r.title}
                              </h4>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-0 shadow-md bg-gradient-to-br from-brand-800 to-brand-950 text-white">
                <CardContent className="pt-6">
                  <BookOpen className="w-8 h-8 text-gold-300 mb-3" />
                  <h3 className="font-display font-bold text-lg mb-2">Stay Informed</h3>
                  <p className="text-sm text-white/80 mb-4 leading-relaxed">
                    Visit the News & Events page for more stories, announcements and upcoming activities at the College.
                  </p>
                  <Link to="/news">
                    <Button size="sm" className="bg-gold-500 hover:bg-gold-600 text-ink-950 w-full">
                      View All News <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </aside>
          </div>
        </div>
      </section>
    </SiteShell>
  )
}
